import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Quote, Star, Send, Loader2, Lock } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value, onChange, size = 18 }) => (
    <div className="flex gap-1" data-testid="review-stars">
        {[1, 2, 3, 4, 5].map((n) => (
            <button
                key={n}
                type="button"
                data-testid={`star-${n}`}
                onClick={() => onChange?.(n)}
                disabled={!onChange}
                aria-label={`${n} stelle`}
                className={onChange ? "cursor-pointer" : "cursor-default"}
            >
                <Star
                    size={size}
                    className={n <= value ? "fill-sky-400 text-sky-400" : "text-slate-700"}
                />
            </button>
        ))}
    </div>
);

const inputCls =
    "w-full bg-[#0F172A]/60 border border-slate-700 text-slate-100 px-4 py-3 text-sm placeholder:text-slate-600 focus:outline-none focus:border-sky-400 transition-colors duration-300";

const EMPTY = { level: "", rating: 0, text: "" };

const Testimonials = () => {
    const [reviews, setReviews] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);
    const [userName] = useState(() => localStorage.getItem("site_name"));
    const [logged] = useState(() => !!localStorage.getItem("site_token"));

    const load = async () => {
        try {
            const res = await axios.get(`${API}/reviews`);
            setReviews(res.data.reviews || []);
        } catch {
            setReviews([]);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (form.rating === 0) {
            toast.error("Scegli un voto da 1 a 5 stelle.");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/reviews`, form, {
                headers: { Authorization: `Bearer ${localStorage.getItem("site_token")}` },
            });
            toast.success("Grazie! La recensione sarà online dopo una rapida verifica.");
            setForm(EMPTY);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("site_token");
                localStorage.removeItem("site_name");
                toast.error("Sessione scaduta: accedi di nuovo per recensire.");
            } else {
                toast.error("Qualcosa è andato storto. Riprova tra poco.");
            }
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="recensioni" className="relative py-32 sm:py-40 border-t border-slate-800 overflow-hidden">
            <div
                className="absolute inset-0 opacity-15 bg-cover bg-center"
                style={{
                    backgroundImage:
                        "url('https://images.unsplash.com/photo-1690788210614-9052cffd8a14?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxzdHVkZW50JTIwc3R1ZHlpbmclMjBkYXJrfGVufDB8fHx8MTc4NjY1ODAxMnww&ixlib=rb-4.1.0&q=85')",
                }}
                aria-hidden="true"
            />
            <div className="absolute inset-0 bg-[#020617]/70" aria-hidden="true" />

            <div className="relative z-10 max-w-[1440px] mx-auto px-6 sm:px-10">
                <SectionHeading
                    index="05"
                    label="Dicono di noi"
                    title={
                        <>
                            Le recensioni qui <em className="text-sky-400">sono tutte vere.</em>
                        </>
                    }
                />
                <Reveal delay={0.15}>
                    <p className="mt-6 max-w-2xl text-slate-300 leading-relaxed">
                        Niente testimonianze inventate: solo recensioni di utenti registrati,
                        lette da noi una a una prima della pubblicazione. Niente spam, solo voci vere.
                    </p>
                </Reveal>

                <div className="mt-16 grid lg:grid-cols-2 gap-16 items-start">
                    <Reveal delay={0.15}>
                        {!logged ? (
                            <div data-testid="review-login-gate" className="border border-slate-800 bg-[#020617]/80 backdrop-blur p-8">
                                <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-slate-500">
                                    <Lock size={14} /> Lascia la tua recensione
                                </p>
                                <p className="mt-5 font-serif text-2xl text-slate-50">
                                    Serve un account gratuito
                                </p>
                                <p className="mt-3 text-slate-400 text-sm leading-relaxed">
                                    Per evitare recensioni false, chiediamo di registrarsi con
                                    email e password prima di scrivere. Ci vogliono 30 secondi.
                                </p>
                                <a
                                    data-testid="review-goto-auth"
                                    href="/accedi"
                                    className="mt-6 inline-flex items-center justify-center gap-3 bg-slate-50 text-[#020617] font-semibold px-8 py-4 hover:bg-sky-400 transition-colors duration-300 w-full"
                                >
                                    Registrati o accedi
                                </a>
                            </div>
                        ) : (
                            <form
                                data-testid="review-form"
                                onSubmit={submit}
                                className="border border-slate-800 bg-[#020617]/80 backdrop-blur p-8 space-y-5"
                            >
                                <p className="font-mono text-xs uppercase tracking-[0.3em] text-slate-500">
                                    Lascia la tua recensione
                                </p>
                                <p className="text-sm text-slate-400">
                                    Pubblichi come <span className="text-slate-100 font-semibold">{userName}</span>
                                </p>
                                <select
                                    data-testid="review-select-level"
                                    required
                                    value={form.level}
                                    onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))}
                                    className={inputCls}
                                >
                                    <option value="" disabled>Chi sei?</option>
                                    <option value="studente medie">Studente medie</option>
                                    <option value="studente superiori">Studente superiori</option>
                                    <option value="genitore">Genitore</option>
                                </select>
                                <div className="flex items-center gap-4">
                                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Voto</span>
                                    <Stars value={form.rating} onChange={(r) => setForm((f) => ({ ...f, rating: r }))} size={26} />
                                </div>
                                <textarea
                                    data-testid="review-input-text"
                                    required
                                    minLength={10}
                                    value={form.text}
                                    onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                                    placeholder="Com'è andata? Raccontalo in due righe (min. 10 caratteri)"
                                    rows={4}
                                    className={`${inputCls} resize-none`}
                                />
                                <button
                                    data-testid="review-submit-button"
                                    type="submit"
                                    disabled={sending}
                                    className="w-full inline-flex items-center justify-center gap-3 bg-slate-50 text-[#020617] font-semibold px-8 py-4 hover:bg-sky-400 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
                                >
                                    {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                                    {sending ? "Invio in corso..." : "Pubblica la recensione"}
                                </button>
                            </form>
                        )}
                    </Reveal>

                    <div className="space-y-6" data-testid="reviews-list">
                        {reviews.length === 0 && (
                            <Reveal delay={0.25}>
                                <div className="border border-dashed border-slate-700 p-10 text-center">
                                    <Quote size={28} className="text-sky-400 mx-auto" strokeWidth={1.5} />
                                    <p className="mt-4 font-serif text-2xl text-slate-50">Ancora nessuna recensione</p>
                                    <p className="mt-2 text-sm text-slate-400">
                                        Il primo a scriverne una potresti essere tu.
                                    </p>
                                </div>
                            </Reveal>
                        )}
                        {reviews.map((r, i) => (
                            <Reveal key={r.id} delay={0.1 + i * 0.08}>
                                <figure
                                    data-testid={`review-card-${i}`}
                                    className="border border-slate-800 bg-[#020617]/80 backdrop-blur p-8 hover:border-sky-400/60 transition-colors duration-500"
                                >
                                    <div className="flex items-center justify-between gap-4">
                                        <Stars value={r.rating} />
                                        <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">{r.level}</span>
                                    </div>
                                    <blockquote className="mt-5 text-slate-300 leading-relaxed">"{r.text}"</blockquote>
                                    <figcaption className="mt-5 border-t border-slate-800 pt-4 font-serif text-xl text-slate-50">
                                        {r.name}
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
export default Testimonials;
